import {zodResolver} from "@hookform/resolvers/zod";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {Alert, Box, Button, CircularProgress, DialogActions, DialogContent} from "@mui/material";
import Grid from "@mui/material/Grid";
import {useElements, useStripe} from "@stripe/react-stripe-js";
import BigNumber from "bignumber.js";
import {RhfRadioGroup} from "mui-rhf-integration";
import React, {type Dispatch, type SetStateAction, useEffect, useMemo, useState} from "react";
import {ErrorBoundary, type FallbackProps} from "react-error-boundary";
import {FormProvider, useForm} from "react-hook-form";
import {z} from "zod";
import ButtonCollection from "@/components/Forms/Fields/ButtonCollection.tsx";
import {useCartContext} from "@/components/Providers/CartProvider.tsx";
import StripeForm from "@/components/StripeForm.tsx";
import {buildRegistrationPrice} from "@/Helpers/buildRegistrationPrice.ts";
import {formatter} from "@/Helpers/formatter.ts";
import {getRegistrationFinancialAid} from "@/Helpers/getRegistrationFinancialAid.ts";
import {
    clearProgramStatusCache,
    type SectionStatus,
    useProgramStatus,
} from "@/hooks/Program/useProgramStatus.ts";
import LocalStorageTimer from "../LocalStorageTimer.tsx";
import {apiEndpoint, jwtContext} from "../Providers/JWTProvider.tsx";
import {
    type CourseSection,
    type Program,
    ProgramsProviderContext,
    STATUS_WAITLISTED,
} from "../Providers/ProgramsProvider.tsx";

type Props = {
    program: Program;
    course: CourseSection;
    sectionStatus: SectionStatus;
    handleClose: (showCancelModal?: boolean, showConfirmModal?: boolean) => void;
    handleLocalCancelOrClose: () => Promise<void>;
    setConfirmationId: (confirmationId: string) => void;
    switchPayment?: boolean;
    courseHasCreditsApplied: boolean;
    show: boolean;
    cancellationPending: boolean;
    setCancellationPending: Dispatch<SetStateAction<boolean>>;
    errorMessage: string;
    hasStripeClientSecret: boolean;
};

const ErrorFallback = ({error, resetErrorBoundary}: FallbackProps) => {
    return (
        <div role="alert">
            <p>There was a issue loading the credit card payments</p>
            <pre>{error.message}</pre>
            <button onClick={resetErrorBoundary} type="reset">
                Try again
            </button>
        </div>
    );
};

export const getPaymentFormSchema = (cost: number) => {
    return z
        .object({
            paymentMethod: z.string(),
            paymentType: z.string().optional(),
            partialPaymentAmount: z.coerce.number().max(cost).optional(),
            tendered: z.coerce
                .number()
                .max(cost + 100, `Tendered amount can't be more then 100 over the payment amount`)
                .optional(),
            checkNumber: z.string().optional(),
        })
        .refine(
            data => {
                if (data.paymentMethod === "fab") {
                    return true;
                }
                return data.paymentType && data.paymentType !== "";
            },
            {path: ["paymentType"], message: "Payment Type is a required field."},
        )
        .refine(
            data => {
                if (data.paymentMethod === "cash") {
                    if (!data.tendered) {
                        return false;
                    }
                }
                return true;
            },
            {
                path: ["tendered"],
                message: "Tendered amount is required for cash payments.",
            },
        )
        .refine(
            data => {
                if (data.paymentMethod !== "inPersonCheck") {
                    return true;
                }
                return data.checkNumber && data.checkNumber.length >= 3;
            },
            {
                path: ["checkNumber"],
                message: "Check Number is required and must be at least 3 characters.",
            },
        );
};

export type PaymentFormSchemaType = z.infer<ReturnType<typeof getPaymentFormSchema>>;

type HeaderProps = {
    sectionStatus: SectionStatus;
    course: CourseSection;
    timeOutCancelRegistration: () => Promise<void>;
    displayCost: string;
};

const FormHeader = ({
    course,
    timeOutCancelRegistration,
    displayCost,
    sectionStatus,
}: HeaderProps) => {
    const user = React.useContext(jwtContext);

    return (
        <>
            {sectionStatus.timeout && (
                <Grid container className="chris-form-bg mx-0 py-1 px-2">
                    <Grid size={{xs: 12}} style={{color: "red", textAlign: "right"}}>
                        You have{" "}
                        <LocalStorageTimer
                            timeout={sectionStatus.timeout}
                            callback={timeOutCancelRegistration}
                        />{" "}
                        to Complete this step.
                    </Grid>
                </Grid>
            )}
            <Grid container className="chris-form-bg mx-0 py-1 px-2">
                <Grid size={{xs: 12}}>
                    All fees must be paid in full by the due date for this activity. Any
                    registrations with outstanding fees after the due date will be cancelled.
                </Grid>
            </Grid>
            <Grid container className="chris-form-bg mx-0 py-1 px-2">
                <Grid size={{xs: 12}}>
                    <div style={{textDecoration: "underline", fontWeight: 800, fontSize: "120%"}}>
                        Activity
                    </div>
                    <div>{user?.nameFirst + " " + user?.nameLast + " - " + user?.studentId}</div>
                    <div>{course.name + ", Section " + course.sectionNumber}</div>
                </Grid>
            </Grid>
            <Grid container className="chris-form-bg mx-0 py-1 px-2">
                <Grid size={{xs: 12}}>
                    <div className="mt-2 mb-2">Cost: {displayCost}</div>
                </Grid>
            </Grid>
        </>
    );
};

const PaymentForm = ({
    course,
    handleClose,
    handleLocalCancelOrClose,
    setConfirmationId,
    switchPayment,
    show,
    cancellationPending,
    setCancellationPending,
    errorMessage,
    hasStripeClientSecret,
    sectionStatus,
    program,
}: Props) => {
    const stripe = useStripe();
    const {addCartItem} = useCartContext();
    const elements = useElements();
    const user = React.useContext(jwtContext);
    const {performRegistrationAction, resetRegistrationStatus} =
        React.useContext(ProgramsProviderContext);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showPayNow, setShowPayNow] = useState<boolean>(false);
    const [submitStripeValues, setSubmitStripeValues] = useState<PaymentFormSchemaType | null>(
        null,
    );
    const [stripeErrorMessage, setStripeErrorMessage] = useState("");
    const {data: sectionStatusMap} = useProgramStatus([program.programUUID]);
    const financialAid = getRegistrationFinancialAid(user);
    const basePrice = buildRegistrationPrice(
        course,
        financialAid,
        sectionStatus.studentOverrideCost,
    );
    const cost = useMemo(
        () =>
            new BigNumber(basePrice)
                .minus(sectionStatus.paidOrPendingAmount)
                .minus(sectionStatus.courseCredits ?? 0)
                .toNumber(),
        [sectionStatus, basePrice],
    );
    const freeMode = cost <= 0;

    useEffect(() => {
        if (freeMode) {
            setShowPayNow(true);
        }
    }, [freeMode]);

    const courseCostIsZero = basePrice === 0;
    const waitlistMode = sectionStatus.status === STATUS_WAITLISTED;

    useEffect(() => {
        setIsProcessing(false);
        setCancellationPending(false);
    }, [show]);

    const confirmFab = async () => {
        const url = new URL("/v1/payment/confirm-fab", apiEndpoint);
        const init: RequestInit = {method: "POST"};
        init.body = JSON.stringify({
            courseSectionId: course.courseSelectionUUID,
            studentId: user?.studentId,
        });

        if (resetRegistrationStatus && switchPayment) {
            await resetRegistrationStatus(course);
        }

        return user?.apiFetch(url.toString(), init);
    };

    const confirmFreeAfterCreditsApplied = async () => {
        const url = new URL("/v1/payment/confirm-free-after-cc", apiEndpoint);
        const init: RequestInit = {method: "POST"};
        init.body = JSON.stringify({
            courseSectionId: course.courseSelectionUUID,
            studentId: user?.studentId,
        });

        return user?.apiFetch(url.toString(), init);
    };

    const confirmWaitlist = async () => {
        const url = new URL("/v1/payment/confirm-waitlist", apiEndpoint);
        const init: RequestInit = {method: "POST"};
        init.body = JSON.stringify({
            courseSectionId: course.courseSelectionUUID,
            studentId: user?.studentId,
        });

        return user?.apiFetch(url.toString(), init);
    };

    const handleFabSubmit = async () => {
        setIsProcessing(true);

        if (performRegistrationAction && sectionStatusMap) {
            const confirmResponse = await confirmFab();

            if (confirmResponse?.ok) {
                const responseJson = await confirmResponse.json();
                setConfirmationId(responseJson.confirmationId);

                await performRegistrationAction(program, course, sectionStatus, sectionStatusMap);
            }
        }

        handleClose(false, true);
        setIsProcessing(false);
    };

    const handleFreeAfterCourseCredits = async () => {
        setIsProcessing(true);

        const confirmResponse = await confirmFreeAfterCreditsApplied();

        if (confirmResponse?.ok) {
            const responseJson = await confirmResponse.json();
            setConfirmationId(responseJson.confirmationId);
            // The registration already exists (credits pending payment) and the
            // confirm endpoint finalizes it server-side. Running performRegistrationAction
            // here would see a "registered" status and DELETE it, so we only refresh status.
            if (user) {
                await clearProgramStatusCache(user.id);
            }
        }

        handleClose(false, true);
        setIsProcessing(false);
    };

    const handleWaitlistSubmit = async () => {
        setIsProcessing(true);

        if (performRegistrationAction && sectionStatusMap) {
            const confirmResponse = await confirmWaitlist();

            if (confirmResponse?.ok) {
                const responseJson = await confirmResponse.json();
                setConfirmationId(responseJson.confirmationId);
                await performRegistrationAction(program, course, sectionStatus, sectionStatusMap);
            }
        }

        handleClose(false, true);
        setIsProcessing(false);
    };

    const cancelRegistration = async (showCancelModal: boolean, reason: string) => {
        if (performRegistrationAction && sectionStatusMap) {
            // cancel registration
            await performRegistrationAction(
                program,
                course,
                sectionStatus,
                sectionStatusMap,
                reason,
            );
        }

        setCancellationPending(false);
        handleClose(showCancelModal);
    };

    const timeOutCancelRegistration = async () => {
        await cancelRegistration(true, "Payment Timeout");
    };

    const schema = getPaymentFormSchema(basePrice);

    // When nothing is owed: a genuinely $0 course uses FAB; a course whose cost
    // is fully covered by credits/payments uses the free-after-course-credits path.
    const freePaymentMethod = courseCostIsZero ? "fab" : "freeAfterCourseCreditsApplied";

    const initialValues = {
        paymentMethod: waitlistMode ? "waitlist" : freeMode ? freePaymentMethod : "",
        paymentType: "full",
        partialPaymentAmount: 0,
    };

    const form = useForm<PaymentFormSchemaType>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
    });

    const paymentMethodWatch = form.watch("paymentMethod");

    const displayCost = formatter.format(cost);

    useEffect(() => {
        if (stripeErrorMessage !== "") {
            setIsProcessing(false);
            setSubmitStripeValues(null);
        }
    }, [stripeErrorMessage]);

    const handelSubmit = async (values: PaymentFormSchemaType) => {
        setStripeErrorMessage("");
        if (values.paymentMethod === "stripe") {
            setSubmitStripeValues(values);
        } else if (values.paymentMethod === "fab") {
            await handleFabSubmit();
        } else if (values.paymentMethod === "freeAfterCourseCreditsApplied") {
            await handleFreeAfterCourseCredits();
        } else if (values.paymentMethod === "waitlist") {
            await handleWaitlistSubmit();
        }
    };

    const payNowOptions = useMemo(() => {
        return [
            {
                value: "stripe",
                label: "Pay Online",
                disabled: cost < 0.5,
                selected: true,
            },
        ];
    }, [cost]);

    if (cancellationPending) {
        return (
            <>
                <DialogContent dividers>
                    <FormHeader
                        course={course}
                        timeOutCancelRegistration={timeOutCancelRegistration}
                        displayCost={displayCost}
                        sectionStatus={sectionStatus}
                    />
                    <Grid container className="mx-0 mt-2">
                        <Grid
                            size={{xs: 12}}
                            className="d-flex flex-row justify-content-space-between justify-content-lg-end"
                        >
                            <Alert severity="warning">
                                Closing this window before payment is provided will cancel your
                                current registration. Do you wish to continue?
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid container className="mx-0 mt-2">
                        <Grid
                            size={{xs: 12}}
                            className="d-flex flex-row justify-content-space-between justify-content-lg-end"
                        >
                            <Button
                                variant="outlined"
                                sx={{
                                    mr: 1,
                                }}
                                onClick={() => setCancellationPending(false)}
                            >
                                No, Provide Payment
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    mr: 1,
                                }}
                                onClick={handleLocalCancelOrClose}
                            >
                                Yes, Cancel Registration
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    }

    return (
        <>
            <DialogContent dividers>
                <FormHeader
                    course={course}
                    timeOutCancelRegistration={timeOutCancelRegistration}
                    displayCost={displayCost}
                    sectionStatus={sectionStatus}
                />
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(handelSubmit)}>
                        {waitlistMode && (
                            <Grid container className="chris-form-bg ml-2 pt-3">
                                <Grid size={{xs: 12}}>
                                    <div
                                        style={{
                                            textDecoration: "underline",
                                            fontWeight: 800,
                                            fontSize: "120%",
                                        }}
                                    >
                                        Payment Options
                                    </div>
                                    <p>
                                        The activity you requested is currently full. You will be
                                        added to the waitlist.
                                    </p>
                                </Grid>
                            </Grid>
                        )}
                        {freeMode && !waitlistMode && (
                            <Grid container className="chris-form-bg ml-2 pt-3">
                                <Grid size={{xs: 12}}>
                                    <div
                                        style={{
                                            textDecoration: "underline",
                                            fontWeight: 800,
                                            fontSize: "120%",
                                        }}
                                    >
                                        Payment Options
                                    </div>
                                    <div className="mt-2 mb-2">
                                        Cost: {displayCost}&nbsp;
                                        {sectionStatus.courseCredits && (
                                            <div style={{display: "inline"}}>
                                                <span
                                                    style={{
                                                        textDecoration: "line-through",
                                                        color: "red",
                                                    }}
                                                >
                                                    {formatter.format(basePrice)}
                                                </span>
                                                <div style={{display: "inline", color: "green"}}>
                                                    &nbsp;Course credits applied!
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {courseCostIsZero ? (
                                        <RhfRadioGroup
                                            control={form.control}
                                            name="paymentMethod"
                                            label=""
                                            options={[
                                                {
                                                    value: "fab",
                                                    label: "Financial Assistance Benefit",
                                                },
                                            ]}
                                        />
                                    ) : (
                                        <RhfRadioGroup
                                            control={form.control}
                                            name="paymentMethod"
                                            label=""
                                            options={[
                                                {
                                                    value: "freeAfterCourseCreditsApplied",
                                                    label: "No Payment needed",
                                                },
                                            ]}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        )}
                        {!freeMode && showPayNow && (
                            <>
                                <Grid container className="chris-form-bg ml-2 py-3">
                                    <Grid size={{xs: 12}}>
                                        <div
                                            style={{
                                                textDecoration: "underline",
                                                fontWeight: 800,
                                                fontSize: "120%",
                                            }}
                                        >
                                            Payment Options
                                        </div>
                                        <div className="mt-2">
                                            Cost: {displayCost}&nbsp;
                                            {sectionStatus.courseCredits && (
                                                <div style={{display: "inline"}}>
                                                    <span
                                                        style={{
                                                            textDecoration: "line-through",
                                                            color: "red",
                                                        }}
                                                    >
                                                        {formatter.format(basePrice)}
                                                    </span>
                                                    <div
                                                        style={{display: "inline", color: "green"}}
                                                    >
                                                        &nbsp;Course credits applied!
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Grid>
                                </Grid>
                                <Grid container>
                                    <Grid size={{xs: 12}}>
                                        <ButtonCollection
                                            name="paymentMethod"
                                            options={payNowOptions}
                                            sx={{
                                                display: "block",
                                                mt: 1,
                                                mb: 2,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                {paymentMethodWatch === "stripe" && hasStripeClientSecret && (
                                    <Grid container>
                                        <Grid size={{xs: 12}}>
                                            {stripe === null || elements === null ? (
                                                <p>
                                                    Loading credit card payment form. If this
                                                    message persists, try reloading the page or
                                                    clearing your browser cache.
                                                </p>
                                            ) : (
                                                <ErrorBoundary
                                                    FallbackComponent={ErrorFallback}
                                                    onReset={() => {
                                                        form.setValue("paymentMethod", "");
                                                    }}
                                                >
                                                    <StripeForm
                                                        cartId={null}
                                                        courseSectionId={course.courseSelectionUUID}
                                                        submitValues={submitStripeValues}
                                                        payNowAmount={cost}
                                                        setIsProcessing={setIsProcessing}
                                                        handleModalClose={handleClose}
                                                        stripeErrorMessage={stripeErrorMessage}
                                                        setStripeErrorMessage={
                                                            setStripeErrorMessage
                                                        }
                                                    />
                                                </ErrorBoundary>
                                            )}
                                        </Grid>
                                    </Grid>
                                )}
                                {errorMessage && (
                                    <Grid size={12} sx={{color: "red"}}>
                                        {errorMessage}
                                    </Grid>
                                )}
                            </>
                        )}
                    </form>
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Grid container>
                    <Grid size={{xs: 12}}>
                        {!isProcessing && (
                            <Button
                                variant="outlined"
                                sx={{
                                    mr: 1,
                                }}
                                onClick={handleLocalCancelOrClose}
                            >
                                Cancel
                            </Button>
                        )}
                        {!(freeMode || isProcessing || waitlistMode) && (
                            <Button
                                variant="contained"
                                sx={{
                                    mr: 1,
                                }}
                                onClick={() => {
                                    addCartItem(course.courseSelectionUUID);
                                    handleClose(false, false);
                                }}
                            >
                                <ShoppingCartIcon />
                                Add to Cart
                            </Button>
                        )}
                        {!showPayNow && (
                            <Button
                                variant="contained"
                                sx={{
                                    mr: 1,
                                }}
                                onClick={() => setShowPayNow(true)}
                            >
                                Pay Now {displayCost}
                            </Button>
                        )}
                        {!isProcessing && showPayNow && (
                            <Button
                                variant="contained"
                                disabled={isProcessing}
                                type={"submit"}
                                onClick={() => {
                                    form.handleSubmit(handelSubmit)();
                                }}
                            >
                                <Box sx={{color: "#fff"}}>
                                    {waitlistMode
                                        ? "Join Waitlist"
                                        : `Pay ${isNaN(cost) ? "" : displayCost} Now`}
                                </Box>
                            </Button>
                        )}
                        {isProcessing && (
                            <Button variant="contained" disabled={true} type={"submit"}>
                                <CircularProgress
                                    size="1rem"
                                    sx={{
                                        mr: 1,
                                        color: "#fff",
                                    }}
                                />
                                <Box sx={{color: "#fff"}}>{"Processing Payment"}</Box>
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </DialogActions>
        </>
    );
};

export default PaymentForm;
